/* eslint-disable max-nested-callbacks */
/* global DEBUG, SSR */
import { get, writable } from "svelte/store";
import { requiresPayment } from "@components/features/Paywall";
import { _ } from "@services/i18n";
import { getStartIntent } from "@services/speakeasy";
import nluSessionId from "@stores/nlu-session-id";
import { featureEnabled } from "@stores/features";
import { experience } from "@stores/global";

export const currentUser = writable(null);
export const hasAuthenticated = writable(false);
export const isPremiumStartIntent = writable(false);
const shouldCheckForStartIntent = writable(true);

export const authenticatedFetch = async (url, init = {}) => {
  if (get(currentUser)) {
    const token = await get(currentUser).getIdToken();
    init.headers = Object.assign({}, init.headers || {}, {
      Authorization: `Bearer ${token}`
    });
  }

  init.headers = Object.assign({}, init.headers || {}, {
    "X-NLU-Session-ID": get(nluSessionId)
  });

  return fetch(url, init).then((response) => {
    const sessionId = response.headers.get("X-NLU-Session-ID");

    if (sessionId) {
      nluSessionId.update(() => sessionId);
    }

    if (!response.ok) {
      return Promise.reject(response);
    }

    return response.json();
  });
};

if (!SSR) {
  import(
    /* webpackChunkName: "firebase" */
    "@services/firebase"
  )
    .then(async ({ default: getFirebaseClient }) => {
      const { auth, firestore } = await getFirebaseClient();
      auth.onAuthStateChanged(async (user) => {
        if (user) {
          const document = await firestore
            .collection("users")
            .doc(user.uid)
            .get();

          if (!document.exists) {
            currentUser.update(() => user);
            hasAuthenticated.set(true);
            shouldCheckForStartIntent.set(false);
            return;
          }

          currentUser.update(() => {
            user.profile = document.data();
            return user;
          });

          // make a post to check for payment status and fetch
          // start intent if necessary as endpoint has no cookie
          // any more to do this up front
          if (featureEnabled("paywall")) {
            if (get(shouldCheckForStartIntent)) {
              shouldCheckForStartIntent.set(false);
              const paymentRequired = await requiresPayment();
              if (!paymentRequired) {
                isPremiumStartIntent.set(true);
                await getStartIntent(get(experience).id, false);
              }
            }
          } else {
            shouldCheckForStartIntent.set(false);
          }
        } else {
          currentUser.update(() => null);
          shouldCheckForStartIntent.set(false);
        }
        hasAuthenticated.set(true);
      });
    })
    .catch(async (error) => {
      if (!DEBUG) {
        const Sentry = await import(
          /* webpackChunkName: "sentry-browser" */
          "@sentry/browser"
        );
        Sentry.captureException(error, {
          tags: {
            functionality: "firebase"
          }
        });
      }

      try {
        // eslint-disable-next-line no-alert
        alert(
          get(_)("errors.generic_please_refresh", {
            default: "Sorry, something went wrong, please refresh your browser"
          })
        );
      } catch (e) {
        // eslint-disable-next-line no-alert
        alert("Sorry, something went wrong, please refresh your browser");
      }
    });
}
